import { ArrowUpRight, FacebookIcon, Instagram, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { WaitlistTrigger } from './WaitlistTrigger';

const FILMSPO_LOGO_WHITE = '/images/filmspo-logo-white.png';

const navigation = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/contact', label: 'Contact' },
];

function ScrollManager() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView();
      });
      return;
    }

    window.scrollTo({ top: 0 });
  }, [hash, pathname]);

  return null;
}

export function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.hash, location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  return (
    <div className="site-shell">
      <ScrollManager />
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <header className="site-header">
        <nav className="site-nav" aria-label="Primary navigation">
          <Link to="/" className="brand-link" aria-label="Filmspo home">
            <img src={FILMSPO_LOGO_WHITE} alt="Filmspo" width="156" height="34" />
          </Link>

          <div className="desktop-navigation">
            <div className="nav-links">
              {navigation.map(item => (
                <NavLink key={item.href} to={item.href} end>
                  {item.label}
                </NavLink>
              ))}
            </div>
            <div className="nav-actions">
              {/* <a className="nav-login" href={`${APP_URL}/login`}>
                Log in
              </a> */}
              <WaitlistTrigger source="nav" className="button button-gold button-compact">
                Join the Waitlist
                <ArrowUpRight aria-hidden="true" />
              </WaitlistTrigger>
            </div>
          </div>

          <button
            ref={menuButtonRef}
            type="button"
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(open => !open)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </nav>

        <div
          id="mobile-navigation"
          className={`mobile-navigation${mobileMenuOpen ? ' is-open' : ''}`}
          hidden={!mobileMenuOpen}
        >
          <nav aria-label="Mobile navigation">
            {navigation.map((item, index) => (
              <NavLink key={item.href} to={item.href} end>
                <span>0{index + 1}</span>
                {item.label}
              </NavLink>
            ))}
            <div className="mobile-nav-actions">
              {/* <a className="button button-outline" href={`${APP_URL}/login`}>
                Log in
              </a> */}
              <WaitlistTrigger source="nav-mobile" className="button button-gold">
                Join the Waitlist
                <ArrowUpRight aria-hidden="true" />
              </WaitlistTrigger>
            </div>
          </nav>
        </div>
      </header>

      <main id="main-content">
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="footer-marquee" aria-hidden="true">
          <span>
            FILM <span>MAKERS</span>
          </span>
          <i />
          <span>
            FILM <span>FESTIVALS</span>
          </span>
          <i />
          <span>
            FILM <span>COMMUNITY</span>
          </span>
        </div>

        <div className="footer-grid page-container">
          <div className="footer-brand">
            <Link to="/" aria-label="Filmspo home">
              <img src={FILMSPO_LOGO_WHITE} alt="Filmspo" width="174" height="38" />
            </Link>
            <p>
              A better festival platform for the artists making the work and the teams championing
              it.
            </p>
          </div>

          {/* <div className="footer-column">
            <h2>Platform</h2>
            <Link to="/">Home</Link>
          </div> */}

          <div className="footer-column">
            <h2>Company</h2>
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>

          <div className="footer-column footer-social">
            <h2>Follow the story</h2>
            <div>
              <a
                href="https://instagram.com/app.filmspo/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Filmspo on Insta"
              >
                <Instagram aria-hidden="true" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61595001786481"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Filmspo on Facebook"
              >
                <FacebookIcon aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <div className="footer-legal page-container">
          <p>&copy; {new Date().getFullYear()} Filmspo. All rights reserved.</p>
          <p>
            Made and owned by{' '}
            <a href="https://circlecitycoders.com" target="_blank" rel="noopener noreferrer">
              Circle City Coders
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
